import { Component, EventEmitter, Input, Output, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MasterService } from 'src/app/services/master.service';
import { MessageService } from 'src/app/services/message.service';
import * as languageLibrary from '../../../../services/language'
import { Subscription } from 'rxjs';
import { clean } from 'src/app/utils/numeric';
import { formatCurrency, formatPercent } from 'src/app/utils/format';
import { User } from 'src/app/models/user.model';
import { SAVE_DEBOUNCE_MILLIS } from 'src/app/constants/sale';
import { Lot } from 'src/app/models/lot.model';
import { StoreService } from 'src/app/services/store.service';
import { COLLECTION_METHODS } from 'src/app/constants/payments';
import { Sale } from 'src/app/models/sale.model';

@Component({
    selector: 'internal-administration',
    templateUrl: './internal-administration.component.html',
    styleUrl: './internal-administration.component.scss',
    standalone: false
})
export class InternalAdministrationComponent implements OnInit, OnDestroy {

    /** Sale data read in the sale detail component. */
    @Input() sale: Sale | null = null

    /** Indicates whether or not the sale is locked (immutable). */
    @Input() locked: boolean = false

    /** Indicates whether or not the sale is BHPH. */
    @Input() bhph: boolean = false

    /** Default collection method as read from the lot setup. */
    @Input() defaultCollectionMethod: string | null = null

    /**  */
    protected form: FormGroup | null = null

    protected lotsAvailable: Lot[] | null = null;

    protected language: any = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    protected words = languageLibrary.language

    /** A list of collectors for the current corp and lot. */
    protected collectors: Array<User> = []

    // A string array of collection methods
    protected collectionMethods: string[] = COLLECTION_METHODS

    /**
     * A debounce timer for modify events. Used to wait for
     * the user to stop typing before we save the changes.
     */
    private watcher: ReturnType<typeof setTimeout> | null = null

    // A handle to navbar event messages so we can unsubscribe later.
    private navbarWatcher: Subscription | null = null

    private subscriptions: Subscription[] = []

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder, private store: StoreService) {
        this.form = this.fb.group({
            lotId: new FormControl<number>(0, []),
            collectionMethod: new FormControl<string | null>(null, []),
            collector: new FormControl<number>(0, []),
            lateFeePercent: new FormControl<string>(formatPercent(0), []),
            businessSource: new FormControl<string | null>(null, []),
            referralFee: new FormControl<string>(formatCurrency(0), []),
            referredBy: new FormControl<string | null>(null, [])
        })
    }

    ngOnInit() {
        this.fetchCollectors()
        this.determineLots()

        this.subscriptions.push(
            this.form.valueChanges.subscribe((changes) => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                this.watcher = setTimeout(() => {
                    this.modify()
                }, SAVE_DEBOUNCE_MILLIS)
            })
        )

        this.navbarWatcher = this.ms.channelComponents$.subscribe(res => {
            if (res.message == 'setPermissions') {
                this.determineLots()
            }
        })

        this.subscriptions.push(
            this.form.get('lotId').valueChanges.subscribe((id) => {
                this.ms.channelComponents$.emit({ message: 'lotChanged', section: 'terms', value: parseInt(id) })
                this.modify()
            })
        )

        this.subscriptions.push(
            this.form.get('collectionMethod').valueChanges.subscribe((method) => {
                this.ms.channelComponents$.emit({ message: 'collectionMethodChanged', section: 'terms', value: method })
                this.modify()
            })
        )
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.locked) {
            this.form.get('lotId').disable({ emitEvent: false })
            this.form.get('collectionMethod').disable({ emitEvent: false })
            if (!this.locked) {
                this.form.get('lotId').enable({ emitEvent: false })
                this.form.get('collectionMethod').enable({ emitEvent: false })
            }
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.sale) {
            if (!this.sale) {
                this.form.reset()
                return
            }

            this.form.patchValue({
                collectionMethod: this.sale.collectionMethod,
                collector: this.sale.collectorId || 0,
                lateFeePercent: formatPercent(this.sale.lateFeePercent),
                referralFee: formatCurrency(this.sale.referralFee)
            }, { emitEvent: false })
        }

        if (changes.defaultCollectionMethod) {
            if (!this.form.get('collectionMethod').value) {
                this.form.get('collectionMethod').setValue(this.defaultCollectionMethod, { emitEvent: false })
            }
        }

        if (changes.bhph) {
            this.determineLots()
        }
    }

    /**
    * Get a list of collectors for the current coproation and lot.
    */
    private fetchCollectors = async () => {
        return await this.master.getAsync(`users/roles/collector`).then(res => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.collectors = res.data.users
        })
    }


    /**
    * Check the user store to get a lot list.
    */
    private determineLots = (): void => {
        this.lotsAvailable = []
        if (!this.store || !this.store.userAccount ||
            !this.store.userAccount['user'] ||
            !this.store.userAccount['user'].lots) {
            return
        }

        const lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)
        if (lotId && !isNaN(lotId) && (lotId > 0)) {
            this.form.get('lotId').setValue(lotId, { emitEvent: false })
        }

        const lots = this.store.userAccount['user'].lots
        for (const lot of lots) {
            if (this.bhph && !lot.bhph) {
                continue
            }

            this.lotsAvailable.push({
                ...new Lot(),
                id: lot.id,
                name: lot.contact.name
            })
        }
    }

    /**
    * Persist changes to the internal admin input boxes.
    */
    protected modify = async () => {
        if (!this.sale) {
            return
        }

        const payload = JSON.parse(JSON.stringify(this.form.getRawValue()))
        payload.lateFeePercent = clean(payload.lateFeePercent)
        payload.referralFee = clean(payload.referralFee)
        payload.collector = parseInt(payload.collector)

        return await this.master.patch(`sales/${this.sale.id}/admin`, payload, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['success'] })
        });
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.navbarWatcher) {
            this.navbarWatcher.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

}
